import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Users, Inbox } from "lucide-react";
import { API } from "../../config";
import { Input } from "../ui/input";
import { CommunityProposalRow } from "./CommunityProposalRow";

const PILL = { pending: "bg-amber-500/12 text-amber-600", published: "bg-emerald-500/12 text-emerald-600", rejected: "bg-rose-500/12 text-rose-500" };

/** Admin → Events → Community: member-submitted proposals (separate from the discovery queue, same actions). */
export default function CommunityProposals({ dm, authHeader, onCounts, onPublished }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/event-proposals`, { params: { status, q }, headers: authHeader() });
      setData(data); onCounts?.(data.counts);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load community proposals"); }
  }, [authHeader, status, q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const act = async (label, fn, ok) => {
    setBusy(label);
    try { const res = await fn(); toast.success(typeof ok === "function" ? ok(res?.data) : ok); await load(); if (label.startsWith("approve")) onPublished?.(); }
    catch (err) { const b = err?.response?.data; const d = b?.code ? b : b?.detail; toast.error(typeof d === "string" ? d : d?.message || d?.missing?.join(", ") || `${label} failed`); }
    setBusy("");
  };
  const post = (path, body = {}) => axios.post(`${API}/admin/event-proposals${path}`, body, { headers: authHeader() });
  const del = (id) => axios.delete(`${API}/admin/event-proposals/${id}`, { headers: authHeader() });
  const counts = data?.counts || {};
  const muted = dm ? "text-slate-500" : "text-[#6b7a7d]";

  return (
    <div className={`px-6 py-4 border-b ${dm ? "border-white/6" : "border-black/5"}`} data-testid="community-proposals">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className={`category-tag text-xs flex items-center gap-1.5 ${dm ? "text-cyan-300" : "text-[#006874]"}`}><Users className="w-3.5 h-3.5" /> Community proposals — reported by members from the Events page</p>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="community-status-filters">
          {["pending", "published", "rejected"].map((s) => (
            <button key={s} onClick={() => setStatus(status === s ? "" : s)} data-testid={`community-filter-${s}`}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 ${status === s ? (dm ? "bg-cyan-400/20 text-cyan-200" : "bg-[#006874]/15 text-[#006874]") : (dm ? "bg-[#0c1011] text-slate-300" : "bg-[#f3f4f5] text-[#3b494c]")}`}>
              {s} <span className={`px-1.5 py-0.5 rounded-full text-[10px] category-tag ${PILL[s]}`} data-testid={`community-count-${s}`}>{counts[s] || 0}</span>
            </button>
          ))}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by proposer email" data-testid="community-search" className={`border-0 text-xs h-8 w-48 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
          <button onClick={load} title="Refresh" data-testid="community-refresh" className={`p-1.5 rounded-full ${dm ? "text-slate-400 hover:bg-white/8" : "text-[#6b7a7d] hover:bg-[#e7e8e9]"}`}><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {!data && <div className="flex justify-center py-6"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>}
      {data && data.items.length === 0 && (
        <p className={`text-xs py-6 text-center flex items-center justify-center gap-2 ${muted}`} data-testid="community-empty"><Inbox className="w-3.5 h-3.5" /> No {status || ""} community proposals.</p>
      )}
      {data && data.items.length > 0 && (
        <div className="space-y-2 max-h-[34rem] overflow-y-auto pr-1">
          {data.items.map((p) => <CommunityProposalRow key={p.id} p={p} dm={dm} busy={busy} act={act} post={post} del={del} reasons={data.reject_reasons} taxonomy={data.taxonomy} />)}
        </div>
      )}
    </div>
  );
}
